import { cn } from "@minket/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

/**
 * The lamp before a status word, in the language of the chain of custody's knots: filled when a
 * state is reached, hollow while the email is on its way, dashed while work is under way.
 *
 * - `neutral`: queued, unknown (a grey lamp);
 * - `primary`: in flight, sent or scheduled: a hollow ring in carbon ink, not the signature ink,
 *   which marks only what a person can do or has decided (the tone keeps its name);
 * - `success`: delivered, verified; `warning`: delayed, pending DNS; `destructive`: bounced,
 *   complained, failed.
 *
 * Every lamp holds 3:1 against the page (contrast.test.ts), and none is shown without its word.
 */
const statusDotVariants = cva("relative inline-flex size-2 shrink-0 rounded-full", {
  variants: {
    tone: {
      neutral:
        "bg-current text-[color-mix(in_oklab,var(--muted-foreground)_70%,var(--background))]",
      primary: "border-[1.5px] border-current text-foreground",
      success: "bg-current text-success",
      warning: "bg-current text-warning",
      destructive: "bg-current text-destructive",
    },
  },
  defaultVariants: { tone: "neutral" },
});

/** The five status inks (DESIGN.md, "Colour"), for tables of status words and tones. */
export type StatusTone = NonNullable<VariantProps<typeof statusDotVariants>["tone"]>;

type StatusDotProps = React.ComponentProps<"span"> &
  VariantProps<typeof statusDotVariants> & {
    /** Read by screen readers. Omit only when adjacent text already states the status. */
    label?: string;
    /**
     * Work under way ("Sending"): the lamp becomes the waiting knot, a dashed ring in its ink.
     * It stays still; Proof has no looping motion, and the word beside it says what is happening.
     */
    pulse?: boolean;
  };

const ringInk: Record<StatusTone, string> = {
  neutral: "text-muted-foreground",
  primary: "text-foreground",
  success: "text-success",
  warning: "text-warning",
  destructive: "text-destructive",
};

/** Colour is never the only signal: pair the lamp with its word. */
function StatusDot({ tone, label, pulse = false, className, ...props }: StatusDotProps) {
  return (
    <span
      data-slot="status-dot"
      data-live={pulse || undefined}
      className={cn("inline-flex items-center", className)}
      {...props}
    >
      {label ? <span className="sr-only">{label}</span> : null}
      {pulse ? (
        <svg
          aria-hidden
          viewBox="0 0 10 10"
          fill="none"
          className={cn("size-2.5 shrink-0 -m-px", ringInk[tone ?? "neutral"])}
        >
          <circle
            cx="5"
            cy="5"
            r="3.9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="2.05 2.05"
          />
        </svg>
      ) : (
        <span aria-hidden className={statusDotVariants({ tone })} />
      )}
    </span>
  );
}

export type { StatusDotProps };
export { StatusDot, statusDotVariants };
